import React, { useCallback, memo } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useSelection } from '../context/SelectionContext';
import { useTheme } from '../context/ThemeContext';
import { Check, ChevronRight } from 'lucide-react';
import { useWizardSteps, useWizardNavigation } from '../hooks/useWizardSteps';

const productPlaceholders = [
  'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=200&h=200&fit=crop',
];

// ===== Option pill button =====
const WizardOption = memo(({ opt, selected, isDark, priceDisplay, onClick }) => (
  <button
    onClick={onClick}
    style={{
      display: 'flex', alignItems: 'center', gap: '12px',
      width: '100%', padding: '14px 16px', borderRadius: '16px',
      border: selected ? '2px solid #B78C3C' : isDark ? '1px solid #404040' : '1px solid rgba(183,140,60,0.15)',
      background: selected ? (isDark ? 'rgba(183,140,60,0.15)' : 'rgba(183,140,60,0.08)') : (isDark ? '#2a2a2a' : '#fff'),
      cursor: 'pointer', transition: 'background-color 0.15s, border-color 0.15s, transform 0.15s',
      textAlign: 'left', transform: selected ? 'scale(1.01)' : 'scale(1)',
    }}
  >
    <div style={{
      width: '24px', height: '24px', borderRadius: '50%', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s',
      background: selected ? 'linear-gradient(135deg, #B78C3C, #D4AF37)' : (isDark ? '#333' : '#eee'),
    }}>
      {selected && <Check size={14} color="#FBF5E8" />}
    </div>
    <span style={{
      flex: 1, fontSize: '15px', fontWeight: selected ? 600 : 400,
      color: isDark ? '#f5f5f5' : '#2C2C2A', fontFamily: "'DM Sans', sans-serif",
    }}>
      {opt.label}
    </span>
    {priceDisplay && (
      <span style={{ fontSize: '14px', fontWeight: 600, flexShrink: 0, color: isDark ? '#D4AF37' : '#B78C3C' }}>
        {priceDisplay}
      </span>
    )}
  </button>
));

const GuidedWizard = ({ product, onComplete, onClose, getImage, index }) => {
  const { language, localize } = useLanguage();
  const { addItem } = useSelection();
  const { isDark } = useTheme();

  const { steps, t } = useWizardSteps(product, language, localize);
  const {
    currentStep, step, totalSteps, slideDir, isLastStep,
    selectedOptions, setSelectedOptions, isCompleting,
    advanceStep, completeWizard, totalPrice
  } = useWizardNavigation(steps, product, addItem, onComplete);

  const cleanName = useCallback((opt) => {
    return localize(opt.name)
      .replace(/^(Ensalada|Principal|Postre|Salad|Main|Dessert|Amanida|Postres|Pan|Bread|Pa|Vin|Wine|Vi):\s*/i, '');
  }, [localize]);

  const isSelected = useCallback((opt) => {
    return selectedOptions.some(s => localize(s.name) === localize(opt.name));
  }, [selectedOptions, localize]);

  const handleSelect = useCallback((opt) => {
    const currentStepData = steps[currentStep];
    if (!currentStepData) return;

    if (currentStepData.multiSelect) {
      setSelectedOptions(prev => {
        const exists = prev.some(s => localize(s.name) === localize(opt.name));
        if (exists) return prev.filter(s => localize(s.name) !== localize(opt.name));
        return [...prev, opt];
      });
    } else if (currentStepData.type === 'variant') {
      if (opt._isBase) {
        setSelectedOptions(prev => prev.filter(s => !s._isVariant));
      } else {
        setSelectedOptions(prev => [...prev.filter(s => !s._isVariant), opt]);
      }
      setTimeout(() => advanceStep(), 50);
    } else if (currentStepData.type === 'choice') {
      if (opt._isBase) {
        const stepOptNames = currentStepData.options.map(o => localize(o.name));
        setSelectedOptions(prev => prev.filter(s => !stepOptNames.includes(localize(s.name)) && !s._isChoice));
      } else {
        const stepOptNames = currentStepData.options.map(o => localize(o.name));
        setSelectedOptions(prev => [...prev.filter(s => !stepOptNames.includes(localize(s.name)) && !s._isChoice), opt]);
      }
      setTimeout(() => advanceStep(), 50);
    } else {
      setSelectedOptions(prev => {
        const filtered = prev.filter(s => s.group !== opt.group);
        return [...filtered, opt];
      });
      setTimeout(() => advanceStep(), 50);
    }
  }, [currentStep, steps, localize, setSelectedOptions, advanceStep]);

  const getPriceDisplay = (opt) => {
    if (opt._isVariant) return `${opt._absolutePrice.toFixed(2)}€`;
    if (opt._isChoice && opt._absolutePrice != null && step._showPrices) return `${opt._absolutePrice.toFixed(2)}€`;
    if (step.type === 'choice') return null;
    if (opt.price_modifier !== 0 && opt.price_modifier != null) return `${opt.price_modifier > 0 ? '+' : ''}${opt.price_modifier.toFixed(2)}€`;
    return null;
  };

  const getOptionSelected = (opt) => {
    if (step.type === 'variant') {
      return opt._isBase ? !selectedOptions.some(s => s._isVariant) : selectedOptions.some(s => localize(s.name) === localize(opt.name));
    }
    return isSelected(opt);
  };

  const imgSrc = getImage ? getImage(product, index || 0) : productPlaceholders[0];

  if (!product || steps.length === 0) return null;

  return (
    <>
      <div onClick={onClose} data-testid="guided-wizard-backdrop"
        style={{ position: 'fixed', inset: 0, zIndex: 95, background: 'rgba(0,0,0,0.45)' }} />

      <div data-testid="guided-wizard" style={{
        position: 'fixed', bottom: 'max(60px, env(safe-area-inset-bottom, 0px) + 60px)',
        left: '10px', right: '10px', zIndex: 96,
        background: isDark ? '#1e1e1e' : '#FBF5E8', borderRadius: '24px',
        padding: '20px 20px 24px', boxShadow: '0 -10px 40px rgba(0,0,0,0.25)',
        animation: 'slideUp 0.15s ease-out', willChange: 'transform, opacity',
        maxHeight: '60vh', overflowY: 'auto', WebkitOverflowScrolling: 'touch',
      }}>
        {/* Handle bar */}
        <div style={{ width: '36px', height: '4px', borderRadius: '2px', background: isDark ? '#555' : '#ccc', margin: '0 auto 16px' }} />

        {/* Product info */}
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '20px' }}>
          <img src={imgSrc} alt={localize(product.name)} decoding="async" style={{
            width: '52px', height: '52px', borderRadius: '14px', objectFit: 'cover', flexShrink: 0,
            border: isDark ? '1px solid #444' : '1px solid rgba(183,140,60,0.2)',
          }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 data-testid="guided-wizard-product-name" style={{
              fontFamily: "'Playfair Display', serif", fontSize: '16px', fontWeight: 600,
              margin: 0, lineHeight: 1.3, color: isDark ? '#f5f5f5' : '#2C2C2A',
            }}>{localize(product.name)}</h3>
            <span style={{ fontSize: '14px', fontWeight: 600, color: isDark ? '#D4AF37' : '#B78C3C' }}>
              {totalPrice.toFixed(2)}€
            </span>
          </div>
        </div>

        {/* Step dots */}
        {totalSteps > 1 && (
          <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', marginBottom: '16px' }}>
            {steps.map((_, idx) => (
              <div key={idx} style={{
                width: idx === currentStep ? '20px' : '6px', height: '6px', borderRadius: '3px',
                transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                background: idx === currentStep ? 'linear-gradient(135deg, #B78C3C, #D4AF37)' : idx < currentStep ? (isDark ? '#555' : '#B78C3C') : (isDark ? '#333' : '#ddd'),
                opacity: idx < currentStep ? 0.5 : 1,
              }} />
            ))}
          </div>
        )}

        {/* Completion state */}
        {isCompleting ? (
          <div style={{ textAlign: 'center', padding: '24px 0', animation: 'fadeIn 0.3s ease' }} data-testid="guided-wizard-done">
            <div style={{
              width: '56px', height: '56px', borderRadius: '50%',
              background: 'linear-gradient(135deg, #B78C3C, #D4AF37)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 12px', animation: 'scaleIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
            }}>
              <Check size={28} color="#FBF5E8" />
            </div>
            <p style={{ fontFamily: "'Playfair Display', serif", fontSize: '18px', fontWeight: 500, color: isDark ? '#f5f5f5' : '#2C2C2A' }}>
              {t('done')}
            </p>
          </div>
        ) : step && (
          <div data-testid={`guided-wizard-step-${currentStep}`} style={{
            animation: slideDir === 'in' ? 'wizardStepIn 0.1s ease-out' : 'wizardStepOut 0.05s ease forwards',
            willChange: 'transform, opacity',
          }}>
            <h4 data-testid="guided-wizard-step-title" style={{
              fontFamily: "'DM Sans', sans-serif", fontSize: '15px', fontWeight: 600,
              marginBottom: '14px', color: isDark ? '#e5e5e5' : '#2C2C2A', textAlign: 'center',
            }}>{step.title}</h4>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '40vh', overflowY: 'auto' }}>
              {step.options.map((opt, idx) => (
                <WizardOption
                  key={idx}
                  opt={{ ...opt, label: cleanName(opt) }}
                  selected={getOptionSelected(opt)}
                  isDark={isDark}
                  priceDisplay={getPriceDisplay(opt)}
                  onClick={() => handleSelect(opt)}
                />
              ))}
            </div>

            {/* Action buttons */}
            <div style={{ marginTop: '16px', display: 'flex', gap: '10px' }}>
              {step.multiSelect && (
                <>
                  <button onClick={isLastStep ? completeWizard : advanceStep} data-testid="guided-wizard-skip"
                    style={{
                      flex: isLastStep ? 2 : 1, padding: '14px', borderRadius: '30px', border: 'none',
                      fontSize: '15px', fontWeight: isLastStep ? 600 : 500, cursor: 'pointer',
                      background: isLastStep ? 'linear-gradient(90deg, #8B4513, #C89B3C)' : (isDark ? '#333' : '#eee'),
                      color: isLastStep ? '#FBF5E8' : (isDark ? '#aaa' : '#8C8C89'),
                      transition: 'transform 0.2s', boxShadow: isLastStep ? '0 4px 15px rgba(0,0,0,0.15)' : 'none',
                    }}>
                    {isLastStep ? t('addAsIs') : t('skip')}
                  </button>
                  {selectedOptions.some(s => step.options.some(o => localize(o.name) === localize(s.name))) && (
                    <button onClick={isLastStep ? completeWizard : advanceStep} data-testid="guided-wizard-continue"
                      style={{
                        flex: 2, padding: '14px', borderRadius: '30px', border: 'none',
                        fontSize: '15px', fontWeight: 600, cursor: 'pointer',
                        background: 'linear-gradient(90deg, #8B4513, #C89B3C)', color: '#FBF5E8',
                        boxShadow: '0 4px 15px rgba(0,0,0,0.15)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                        transition: 'transform 0.2s',
                      }}>
                      {isLastStep ? t('add') : t('next')}
                      {!isLastStep && <ChevronRight size={16} />}
                    </button>
                  )}
                </>
              )}
              {!step.multiSelect && !step.required && step.type === 'group' && (
                <button onClick={advanceStep} data-testid="guided-wizard-skip"
                  style={{
                    width: '100%', padding: '14px', borderRadius: '30px', border: 'none',
                    fontSize: '14px', fontWeight: 500, cursor: 'pointer',
                    background: 'transparent', color: isDark ? '#777' : '#8C8C89',
                  }}>
                  {t('skip')}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default GuidedWizard;
